import { hrApi } from '../config/apiClient';
import { HRAPI_URL } from '../config/api';
import { CVFormData, CVApiResponse } from '../types/cv.types';

// HR/CV modulu gts_hr_api'ye tasindi (P9). Taban URL artik HR API.
const API_BASE_URL = HRAPI_URL + '/api/HrCv';

// gts_hr_api PascalCase JSON dondurur; CV bilesenleri/types camelCase bekliyor.
// Cevaplari derin camelCase'e normalize ederek mevcut tuketicileri bozmuyoruz.
const camelizeDeep = (input: any): any => {
  if (Array.isArray(input)) return input.map(camelizeDeep);
  if (input !== null && typeof input === 'object') {
    return Object.keys(input).reduce((acc: any, key) => {
      const ck = key.charAt(0).toLowerCase() + key.slice(1);
      acc[ck] = camelizeDeep(input[key]);
      return acc;
    }, {});
  }
  return input;
};

// Company ID'yi localStorage'dan al (Multi-tenant için)
const getCompanyId = (): string => {
  return localStorage.getItem('companyId') || '1'; // Varsayılan company_id = '1'
};

// Axios instance oluştur (her istekte company_id header ekler)
const getAxiosConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    'company_id': getCompanyId()
  }
});

class CVService {
  /**
   * CV Oluşturma Servisi
   * Frontend'den gelen iç içe (nested) veriyi Backend'in beklediği düz (flat) yapıya çevirir.
   */
  async createCV(formData: CVFormData): Promise<CVApiResponse> {
    try {
      console.log("🚀 CV Gönderiliyor...", formData);
      
      // Helper function: Boş string'leri undefined'a çevir
      const toUndefinedIfEmpty = (value: any): any => {
        if (typeof value === 'string' && value.trim() === '') return undefined;
        return value;
      };
      
      // ZORUNLU ALANLARI KONTROL ET
      if (!formData.personalInfo.firstName?.trim()) {
        throw new Error('Ad alanı zorunludur');
      }
      if (!formData.personalInfo.lastName?.trim()) {
        throw new Error('Soyad alanı zorunludur');
      }
      if (!formData.personalInfo.email?.trim()) {
        throw new Error('E-posta alanı zorunludur');
      }
      if (!formData.personalInfo.phone?.trim()) {
        throw new Error('Cep telefonu alanı zorunludur');
      }
      if (!formData.kvkkConsent) {
        throw new Error('KVKK onayı zorunludur');
      }
      
      console.log("✅ Zorunlu alan kontrolleri başarılı");
      
      // 1. MAPPING: Frontend verisini Backend formatına çeviriyoruz
      // Backend PascalCase JSON kullanıyor (ASP.NET Core custom configuration)
      const apiPayload: any = {
        // --- Kişisel Bilgiler (Düzleştirildi) ---
        FirstName: formData.personalInfo.firstName.trim(),
        LastName: formData.personalInfo.lastName.trim(),
        TcIdentityNo: toUndefinedIfEmpty(formData.personalInfo.tckn),
        Email: formData.personalInfo.email.trim(),
        MobilePhone: formData.personalInfo.phone.trim(),
        BirthPlace: toUndefinedIfEmpty(formData.personalInfo.birthPlace),
        Gender: toUndefinedIfEmpty(formData.personalInfo.gender),
        MaritalStatus: toUndefinedIfEmpty(formData.personalInfo.maritalStatus),
        BloodType: toUndefinedIfEmpty(formData.personalInfo.bloodType),
        Nationality: toUndefinedIfEmpty(formData.personalInfo.nationality),
        
        // --- İletişim (Düzleştirildi) ---
        HomeAddress: toUndefinedIfEmpty(formData.personalInfo.address),
        HomePhone: toUndefinedIfEmpty(formData.personalInfo.homePhone),
        WorkPhone: toUndefinedIfEmpty(formData.personalInfo.workPhone),
        City: toUndefinedIfEmpty(formData.personalInfo.city),
        EmergencyContactName: toUndefinedIfEmpty(formData.personalInfo.emergencyContactName),
        EmergencyContactPhone: toUndefinedIfEmpty(formData.personalInfo.emergencyContactPhone),

        // --- Askerlik ---
        MilitaryStatus: toUndefinedIfEmpty(formData.militaryStatus.status),
        MilitaryReason: toUndefinedIfEmpty(formData.militaryStatus.exemptionReason),

        // --- İş Tercihleri ---
        DesiredPosition: toUndefinedIfEmpty(formData.jobPreferences.desiredPosition),
        DesiredSalary: formData.jobPreferences.desiredSalary ? Number(formData.jobPreferences.desiredSalary) : undefined,
        ShiftWorkAcceptable: !!formData.jobPreferences.canWorkNightShift,
        OvertimeAcceptable: !!formData.jobPreferences.canWorkOvertime,
        CanTravel: !!formData.jobPreferences.canTravel,
        CanRelocate: !!formData.jobPreferences.canRelocate,

        // --- Özel Bilgiler ---
        PreviousApplication: !!formData.specialInfo?.previousApplication,
        DriverLicense: toUndefinedIfEmpty(formData.specialInfo?.driverLicense),
        HasHealthIssue: !!formData.specialInfo?.hasHealthIssue,
        HealthIssueDetails: toUndefinedIfEmpty(formData.specialInfo?.healthIssueDetails),
        TravelRestriction: !!formData.specialInfo?.travelRestriction,
        TravelRestrictionDetails: toUndefinedIfEmpty(formData.specialInfo?.travelRestrictionDetails),
        CriminalRecord: !!formData.specialInfo?.criminalRecord,
        CriminalRecordDetails: toUndefinedIfEmpty(formData.specialInfo?.criminalRecordDetails),
        Smoker: !!formData.specialInfo?.smoker,

        // --- Fiziksel Özellikler ---
        Height: formData.physicalAttributes?.height ? Number(formData.physicalAttributes.height) : undefined,
        Weight: formData.physicalAttributes?.weight ? Number(formData.physicalAttributes.weight) : undefined,
        ShoeSize: toUndefinedIfEmpty(formData.physicalAttributes?.shoeSize),
        ClothingSize: toUndefinedIfEmpty(formData.physicalAttributes?.clothingSize),

        // --- Diğer Bilgiler ---
        HobbiesAndInterests: toUndefinedIfEmpty(formData.additionalInfo?.hobbiesAndInterests),
        OtherInfo: toUndefinedIfEmpty(formData.additionalInfo?.otherInfo),

        // --- İlişkisel Listeler (Array olarak gönderilir) ---
        // Backend PascalCase kullanıyor
        Educations: formData.educations
            .filter(edu => edu.schoolName?.trim() && edu.degree?.trim()) // Sadece geçerli olanları gönder
            .map(edu => ({
                EducationLevel: edu.degree.trim(), // REQUIRED
                SchoolName: edu.schoolName.trim(), // REQUIRED
                Department: toUndefinedIfEmpty(edu.department),
                StartYear: edu.startYear ? Number(edu.startYear) : undefined,
                EndYear: edu.endYear ? Number(edu.endYear) : undefined,
                GraduationYear: edu.isGraduated && edu.endYear ? Number(edu.endYear) : undefined,
                Gpa: edu.gpa ? Number(edu.gpa) : undefined,
                IsGraduated: !!edu.isGraduated
            })),

        Experiences: formData.experiences
            .filter(exp => {
                // Sadece geçerli experience'ları gönder
                if (!exp.companyName?.trim() || !exp.position?.trim()) {
                    console.log(`⚠️ Experience atlandı (eksik bilgi):`, exp.companyName || 'İsimsiz');
                    return false;
                }
                if (!exp.startDate) {
                    console.log(`⚠️ Experience atlandı (startDate yok):`, exp.companyName);
                    return false;
                }
                
                // startDate geçerli bir tarih mi kontrol et
                const date = new Date(exp.startDate);
                if (isNaN(date.getTime())) {
                    console.warn(`⚠️ Experience atlandı (geçersiz tarih):`, exp.companyName, exp.startDate);
                    return false;
                }
                
                console.log(`✅ Experience kabul edildi:`, exp.companyName);
                return true;
            })
            .map(exp => ({
                CompanyName: exp.companyName!.trim(),
                Position: exp.position!.trim(),
                StartDate: new Date(exp.startDate!).toISOString(),
                EndDate: exp.endDate ? new Date(exp.endDate).toISOString() : undefined,
                IsCurrent: !!exp.isCurrentJob,
                NetSalary: exp.netSalary ? Number(exp.netSalary) : undefined,
                LeavingReason: toUndefinedIfEmpty(exp.leavingReason),
                JobDescription: toUndefinedIfEmpty(exp.description)
            })),

        Languages: formData.languages
            .filter(lang => lang.name?.trim() && lang.level?.trim()) // REQUIRED alanlar
            .map(lang => ({
                LanguageName: lang.name!.trim(), // REQUIRED
                ProficiencyLevel: lang.level!.trim(), // REQUIRED
                Certificate: undefined, // Backend'de var ama frontend'de kullanmıyoruz
                Institution: toUndefinedIfEmpty(lang.institution),
                CertificateDate: lang.certificateDate ? new Date(lang.certificateDate).toISOString() : undefined
            })),

        ComputerSkills: formData.computerSkills
            .filter(skill => skill.name?.trim() && skill.level?.trim()) // Boş name'leri filtrele
            .map(skill => ({
                ProgramName: skill.name!.trim(), // REQUIRED
                ProficiencyLevel: skill.level!.trim(), // REQUIRED
                Institution: toUndefinedIfEmpty(skill.institution),
                CertificateDate: skill.certificateDate ? new Date(skill.certificateDate).toISOString() : undefined
            })),

        Certifications: (formData.certifications || [])
            .filter(cert => cert.institution?.trim() && cert.subject?.trim()) // REQUIRED alanlar
            .map(cert => ({
                Institution: cert.institution!.trim(), // REQUIRED
                Subject: cert.subject!.trim(), // REQUIRED
                DurationHours: cert.durationHours ? Number(cert.durationHours) : undefined,
                CertificationDate: cert.certificationDate ? new Date(cert.certificationDate).toISOString() : undefined,
                CertificateFile: undefined
            })),

        // Referanslar - Backend CreateHrCvReferenceDto kullanıyor (Name, Company, Position REQUIRED)
        References: formData.references
            .filter(ref => ref.fullName?.trim() && ref.company?.trim() && ref.position?.trim()) // REQUIRED alanlar
            .map(ref => ({
                Name: ref.fullName!.trim(), // REQUIRED
                Company: ref.company!.trim(), // REQUIRED
                Position: ref.position!.trim(), // REQUIRED
                Phone: toUndefinedIfEmpty(ref.phone)
                // Email backend DTO'da yok!
            })),

        KvkkConsent: true // REQUIRED - formData'dan gelen değer zaten kontrol edildi
        // CompanyId controller tarafından header'dan alınıyor, body'de göndermeye gerek yok
      };
      
      // Tarihleri sadece değer varsa ekle (null/undefined gönderme)
      if (formData.personalInfo.birthDate) {
        apiPayload.BirthDate = new Date(formData.personalInfo.birthDate).toISOString();
      }
      if (formData.militaryStatus.completionDate) {
        apiPayload.MilitaryDate = new Date(formData.militaryStatus.completionDate).toISOString();
      }
      if (formData.militaryStatus.postponeDate) {
        apiPayload.MilitaryPostponeDate = new Date(formData.militaryStatus.postponeDate).toISOString();
      }
      if (formData.jobPreferences.startDate) {
        apiPayload.AvailableStartDate = new Date(formData.jobPreferences.startDate).toISOString();
      }
      // Güvenlik Kartı Bitiş Tarihi (sadece Güvenlik pozisyonu için)
      if (formData.jobPreferences.securityCardExpiryDate) {
        apiPayload.SecurityCardExpiryDate = new Date(formData.jobPreferences.securityCardExpiryDate).toISOString();
      }
      if (formData.jobPreferences.securityCardExpiryDate) {
        apiPayload.SecurityCardExpiryDate = new Date(formData.jobPreferences.securityCardExpiryDate).toISOString();
      }

      console.log("📦 Hazırlanan Paket (Payload):", apiPayload);
      console.log("📊 Experience sayısı:", apiPayload.Experiences?.length || 0);
      console.log("📊 Education sayısı:", apiPayload.Educations?.length || 0);
      console.log("📊 Zorunlu alanlar - DETAYLI:");
      console.log("  ✅ FirstName:", apiPayload.FirstName, `(type: ${typeof apiPayload.FirstName})`);
      console.log("  ✅ LastName:", apiPayload.LastName, `(type: ${typeof apiPayload.LastName})`);
      console.log("  ✅ Email:", apiPayload.Email, `(type: ${typeof apiPayload.Email})`);
      console.log("  ✅ MobilePhone:", apiPayload.MobilePhone, `(type: ${typeof apiPayload.MobilePhone})`);
      console.log("  ✅ KvkkConsent:", apiPayload.KvkkConsent, `(type: ${typeof apiPayload.KvkkConsent})`);

      // Backend doğrudan CreateHrCvDto bekliyor (wrapper YOK)
      console.log("📤 Request Body hazırlandı (direkt payload, wrapper YOK)");
      console.log("📤 Gönderilecek JSON:", JSON.stringify(apiPayload, null, 2));
      console.log("📤 Request Headers:", getAxiosConfig().headers);
      
      const response = await hrApi.post(API_BASE_URL, apiPayload, getAxiosConfig());
      
      console.log("✅ Başarılı Yanıt:", response.data);
      return camelizeDeep(response.data);

    } catch (error: any) {
      console.error('❌ API Hatası:', error.response?.data || error.message);
      
      // Validation hatalarını detaylı logla
      if (error.response?.data?.errors) {
        console.error('🔍 Validation Hataları:', error.response.data.errors);
        console.table(error.response.data.errors);
        
        // Her bir hatayı tek tek yazdır
        Object.keys(error.response.data.errors).forEach(key => {
          console.error(`  ❗ ${key}:`, error.response.data.errors[key]);
        });
      }
      
      // Tam hata response'unu da logla
      if (error.response) {
        console.error('📍 HTTP Status:', error.response.status);
        console.error('📍 Response Headers:', error.response.headers);
        console.error('📍 Full Response Data:', JSON.stringify(error.response.data, null, 2));
      }
      
      // Hata detayını fırlat ki UI yakalasın
      throw error;
    }
  }

  /**
   * Tüm CV'leri Listele
   */
  async getAllCVs(): Promise<CVApiResponse> {
    try {
      console.log("📋 CV Listesi çekiliyor...");
      const response = await hrApi.get(API_BASE_URL, getAxiosConfig());
      console.log("✅ CV Listesi alındı:", response.data);
      return camelizeDeep(response.data);
    } catch (error: any) {
      console.error('❌ CV Listesi Hatası:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * ID'ye göre CV Detayını Getir
   */
  async getCVById(id: string): Promise<CVApiResponse> {
    try {
      console.log(`🔍 CV Detayı çekiliyor: ${id}`);
      const response = await hrApi.get(`${API_BASE_URL}/${id}`, getAxiosConfig());
      console.log("✅ CV Detayı alındı - RAW RESPONSE:", JSON.stringify(response.data, null, 2));
      console.log("📊 Educations:", response.data?.data?.educations || response.data?.educations);
      console.log("📊 Experiences:", response.data?.data?.experiences || response.data?.experiences);
      console.log("📊 Languages:", response.data?.data?.languages || response.data?.languages);
      console.log("📊 ComputerSkills:", response.data?.data?.computerSkills || response.data?.computerSkills);
      console.log("📊 Certifications:", response.data?.data?.certifications || response.data?.certifications);
      console.log("📊 References:", response.data?.data?.references || response.data?.references);
      return camelizeDeep(response.data);
    } catch (error: any) {
      console.error('❌ CV Detayı Hatası:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * CV Arama ve Filtreleme
   */
  async searchCVs(searchParams: {
    keyword?: string;
    position?: string;
    city?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<CVApiResponse> {
    try {
      console.log("🔍 CV Arama yapılıyor:", searchParams);
      const config = { ...getAxiosConfig(), params: searchParams };
      const response = await hrApi.get(`${API_BASE_URL}/search`, config);
      console.log("✅ Arama sonuçları alındı:", response.data);
      return camelizeDeep(response.data);
    } catch (error: any) {
      console.error('❌ CV Arama Hatası:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * CV Sil
   * Backend POST /api/HrCv/delete/{id} endpoint'ini kullanıyor
   */
  async deleteCV(id: string): Promise<CVApiResponse> {
    try {
      console.log(`🗑️ CV siliniyor, ID: ${id}`);
      console.log(`🌐 API Base URL: ${API_BASE_URL}`);
      console.log(`🏢 Company ID: ${getCompanyId()}`);
      
      // Backend'iniz POST /api/HrCv/delete/{id} kullanıyor
      const deleteUrl = `${API_BASE_URL}/delete/${id}`;
      console.log(`✨ Silme isteği gönderiliyor: POST ${deleteUrl}`);
      
      const response = await hrApi.post(deleteUrl, {}, getAxiosConfig());
      const data = camelizeDeep(response.data);
      console.log("✅ CV silindi:", data);
      
      // Backend response kontrolü
      if (data && data.success !== false) {
        return data;
      } else {
        throw new Error(data?.message || 'CV silme işlemi başarısız');
      }
      
    } catch (error: any) {
      console.error('❌ CV Silme Hatası:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Kullanıcı dostu hata mesajı
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error
        || error.message 
        || 'CV silme işlemi başarısız oldu';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * CV Güncelle
   * Sunucuda PUT metodu çalışmadığı için POST kullanılır
   */
  async updateCV(id: string, formData: CVFormData): Promise<CVApiResponse> {
    try {
      console.log(`📝 CV güncelleniyor: ${id}`, formData);
      
      // Aynı mapping'i createCV'deki gibi yapıyoruz
      const apiPayload = {
        id: id, // ID'yi de ekliyoruz
        firstName: formData.personalInfo.firstName,
        lastName: formData.personalInfo.lastName,
        tcIdentityNo: formData.personalInfo.tckn,
        email: formData.personalInfo.email,
        mobilePhone: formData.personalInfo.phone,
        birthDate: formData.personalInfo.birthDate ? new Date(formData.personalInfo.birthDate).toISOString() : null,
        birthPlace: formData.personalInfo.birthPlace,
        gender: formData.personalInfo.gender,
        maritalStatus: formData.personalInfo.maritalStatus,
        bloodType: formData.personalInfo.bloodType,
        nationality: formData.personalInfo.nationality,
        homeAddress: formData.personalInfo.address,
        homePhone: formData.personalInfo.homePhone,
        workPhone: formData.personalInfo.workPhone,
        city: formData.personalInfo.city,
        emergencyContactName: formData.personalInfo.emergencyContactName,
        emergencyContactPhone: formData.personalInfo.emergencyContactPhone,
        militaryStatus: formData.militaryStatus.status,
        militaryDate: formData.militaryStatus.completionDate ? new Date(formData.militaryStatus.completionDate).toISOString() : null,
        militaryPostponeDate: formData.militaryStatus.postponeDate ? new Date(formData.militaryStatus.postponeDate).toISOString() : null,
        militaryReason: formData.militaryStatus.exemptionReason,
        desiredPosition: formData.jobPreferences.desiredPosition,
        desiredSalary: Number(formData.jobPreferences.desiredSalary) || 0,
        availableStartDate: formData.jobPreferences.startDate ? new Date(formData.jobPreferences.startDate).toISOString() : null,
        shiftWorkAcceptable: formData.jobPreferences.canWorkNightShift,
        overtimeAcceptable: formData.jobPreferences.canWorkOvertime,
        canTravel: formData.jobPreferences.canTravel,
        canRelocate: formData.jobPreferences.canRelocate,
        previousApplication: formData.specialInfo?.previousApplication || false,
        driverLicense: formData.specialInfo?.driverLicense,
        hasHealthIssue: formData.specialInfo?.hasHealthIssue || false,
        healthIssueDetails: formData.specialInfo?.healthIssueDetails,
        travelRestriction: formData.specialInfo?.travelRestriction || false,
        travelRestrictionDetails: formData.specialInfo?.travelRestrictionDetails,
        criminalRecord: formData.specialInfo?.criminalRecord || false,
        criminalRecordDetails: formData.specialInfo?.criminalRecordDetails,
        smoker: formData.specialInfo?.smoker || false,
        height: Number(formData.physicalAttributes?.height) || 0,
        weight: Number(formData.physicalAttributes?.weight) || 0,
        shoeSize: formData.physicalAttributes?.shoeSize,
        clothingSize: formData.physicalAttributes?.clothingSize,
        hobbiesAndInterests: formData.additionalInfo?.hobbiesAndInterests,
        otherInfo: formData.additionalInfo?.otherInfo,
        educations: formData.educations.map(edu => ({
            schoolName: edu.schoolName,
            department: edu.department,
            educationLevel: edu.degree,
            startYear: Number(edu.startYear),
            endYear: Number(edu.endYear),
            isGraduated: edu.isGraduated,
            gpa: edu.gpa ? Number(edu.gpa) : undefined
        })),
        experiences: formData.experiences.map(exp => ({
            companyName: exp.companyName,
            position: exp.position,
            startDate: exp.startDate ? new Date(exp.startDate).toISOString() : null,
            endDate: exp.endDate ? new Date(exp.endDate).toISOString() : null,
            isCurrent: exp.isCurrentJob,
            netSalary: Number(exp.netSalary) || 0,
            leavingReason: exp.leavingReason,
            jobDescription: exp.description
        })),
        languages: formData.languages.map(lang => ({
            languageName: lang.name,
            proficiencyLevel: lang.level,
            institution: lang.institution,
            certificateDate: lang.certificateDate ? new Date(lang.certificateDate).toISOString() : null
        })),
        computerSkills: formData.computerSkills.map(skill => ({
            programName: skill.name,
            proficiencyLevel: skill.level,
            institution: skill.institution,
            certificateDate: skill.certificateDate ? new Date(skill.certificateDate).toISOString() : null
        })),
        certifications: formData.certifications?.map(cert => ({
            institution: cert.institution,
            subject: cert.subject,
            durationHours: Number(cert.durationHours) || 0,
            certificationDate: cert.certificationDate ? new Date(cert.certificationDate).toISOString() : null
        })) || [],
        references: formData.references.map(ref => ({
            name: ref.fullName,
            company: ref.company,
            position: ref.position,
            phone: ref.phone,
            email: ref.email || undefined
        })),
        kvkkConsent: formData.kvkkConsent,
        companyId: getCompanyId()
      };

      // Yöntem 1: POST /api/HrCv/Update/{id}
      try {
        console.log(`Deneme 1: POST ${API_BASE_URL}/Update/${id}`);
        const response = await hrApi.post(`${API_BASE_URL}/Update/${id}`, apiPayload, getAxiosConfig());
        console.log("✅ CV güncellendi (Yöntem 1):", response.data);
        return camelizeDeep(response.data);
      } catch (error: any) {
        console.warn('⚠️ Yöntem 1 başarısız, Yöntem 2 deneniyor...', error.response?.status);
        
        // Yöntem 2: POST /api/HrCv/{id}/Update
        try {
          console.log(`Deneme 2: POST ${API_BASE_URL}/${id}/Update`);
          const response = await hrApi.post(`${API_BASE_URL}/${id}/Update`, apiPayload, getAxiosConfig());
          console.log("✅ CV güncellendi (Yöntem 2):", response.data);
          return camelizeDeep(response.data);
        } catch (error2: any) {
          console.warn('⚠️ Yöntem 2 başarısız, Yöntem 3 deneniyor...', error2.response?.status);
          
          // Yöntem 3: POST /api/HrCv/Update (body içinde id)
          try {
            console.log(`Deneme 3: POST ${API_BASE_URL}/Update`);
            const response = await hrApi.post(`${API_BASE_URL}/Update`, apiPayload, getAxiosConfig());
            console.log("✅ CV güncellendi (Yöntem 3):", response.data);
            return camelizeDeep(response.data);
          } catch (error3: any) {
            console.error('❌ Tüm güncelleme yöntemleri başarısız oldu');
            throw error3;
          }
        }
      }
    } catch (error: any) {
      console.error('❌ CV Güncelleme Hatası:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }
}

// Tek bir instance (Singleton) olarak dışarı açıyoruz
export default new CVService();